import React, { useState } from 'react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { Heading, H2, Avatar, UserDisplayName } from '../../components';
import { getReviewsSummary } from '../../util/listingPageContent';
import { IconStarFill } from './ListingIcons';

import css from './ListingPage.module.css';

const INITIAL_REVIEWS_COUNT = 3;

/**
 * Single review row matching Local Hire Figma (node 469:1006).
 *
 * @component
 */
const ListingReviewItem = props => {
  const { review, intl } = props;
  const rating = review?.attributes?.rating;
  const content = review?.attributes?.content;
  const ratingLabel =
    typeof rating === 'number' && !Number.isNaN(rating) ? rating.toFixed(1) : null;

  return (
    <li className={css.listingReviewItem}>
      <div className={css.listingReviewHeader}>
        <Avatar rootClassName={css.listingReviewAvatar} user={review.author} />
        <div className={css.listingReviewMeta}>
          <p className={css.listingReviewName}>
            <UserDisplayName user={review.author} intl={intl} />
          </p>
          {ratingLabel ? (
            <div className={css.listingReviewRating}>
              <span className={css.listingReviewStar}>
                <IconStarFill />
              </span>
              <span className={css.listingReviewRatingValue}>{ratingLabel}</span>
            </div>
          ) : null}
        </div>
      </div>
      {content ? <p className={css.listingReviewContent}>{content}</p> : null}
    </li>
  );
};

/**
 * Reviews section — Figma layout: centered title, average, bordered review rows, show more.
 *
 * @component
 */
const SectionReviews = props => {
  const intl = useIntl();
  const { reviews = [], fetchReviewsError } = props;
  const [showAll, setShowAll] = useState(false);

  const hasReviews = reviews && reviews.length > 0;
  if (!hasReviews && !fetchReviewsError) {
    return null;
  }

  const reviewsSummary = getReviewsSummary(reviews);
  const visibleReviews = showAll ? reviews : reviews.slice(0, INITIAL_REVIEWS_COUNT);
  const hasMoreReviews = reviews.length > INITIAL_REVIEWS_COUNT;

  return (
    <section className={css.sectionReviews}>
      <div className={css.reviewsHeader}>
        <Heading as="h2" rootClassName={css.reviewsTitle}>
          <FormattedMessage id="ListingPage.reviewsForEquipmentTitle" />
        </Heading>

        {reviewsSummary ? (
          <div className={css.reviewsSummary}>
            <span className={css.reviewsStar}>
              <IconStarFill />
            </span>
            <p className={css.reviewsAverage}>{reviewsSummary.average.toFixed(2)}</p>
            <span className={css.reviewsDot} aria-hidden="true" />
            <p className={css.reviewsCount}>
              <FormattedMessage
                id="ListingPage.reviewsCountPlain"
                values={{ count: reviewsSummary.count }}
              />
            </p>
          </div>
        ) : (
          <p className={css.reviewsEmpty}>
            <FormattedMessage id="ListingPage.noReviewsYet" />
          </p>
        )}
      </div>

      {fetchReviewsError ? (
        <H2 className={css.errorText}>
          <FormattedMessage id="ListingPage.reviewsError" />
        </H2>
      ) : null}

      {visibleReviews.length ? (
        <ul className={css.listingReviewList}>
          {visibleReviews.map(review => (
            <ListingReviewItem key={review.id.uuid} review={review} intl={intl} />
          ))}
        </ul>
      ) : null}

      {hasMoreReviews && !showAll ? (
        <button
          type="button"
          className={css.showMoreReviewsButton}
          onClick={() => setShowAll(true)}
        >
          <FormattedMessage id="ListingPage.showMoreReviews" />
        </button>
      ) : null}
    </section>
  );
};

export default SectionReviews;
